import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import '../kpi-dashboard/chartjs-register';
import type { FilterChip } from '../../components/ActiveFilterChips';
import {
  BW_ALL_TASK_TYPES_FILTER_VALUE,
  resolveBandwidthEffectiveExportDateRange,
} from './bandwidthDashboardApi';
import { BANDWIDTH_CATEGORIES, type BandwidthCategoryId } from './types';
import { useBandwidthFilters } from './useBandwidthFilters';
import { useBandwidthDashboard } from './useBandwidthDashboard';
import { normalizeAssignedToUserIds } from './bandwidthAssigneeFilter';
import type { BandwidthCrossFilterSlice } from './bandwidthCrossFilter';
import { bandwidthCrossFilterQueryKey } from './bandwidthCrossFilter';
import BandwidthFilterBar from './BandwidthFilterBar';
import { BandwidthFetchingIndicator } from './BandwidthDashboardLoader';
import { useBandwidthUpdatingIndicator } from './useBandwidthUpdatingIndicator';
import BandwidthClassicDashboardGrid from './BandwidthClassicDashboardGrid';
import BandwidthCountTimeGrid from './BandwidthCountTimeGrid';
import BandwidthDatesGrid from './BandwidthDatesGrid';
import BandwidthUsersGrid from './BandwidthUsersGrid';
import type { UsersChartDrill } from './bandwidthUsersDrill';
import type { BandwidthClassicDrillDown } from './bandwidthClassicDrill';
const WEEKDAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;

const Bandwidth: React.FC = () => {
  const [activeCategoryId, setActiveCategoryId] = useState<BandwidthCategoryId>('dashboard');
  const [crossFilter, setCrossFilter] = useState<BandwidthCrossFilterSlice>(null);
  const [datesDrillMonth, setDatesDrillMonth] = useState<string | null>(null);
  const [datesDrillWeekday, setDatesDrillWeekday] = useState<number | null>(null);
  const [usersChartDrill, setUsersChartDrill] = useState<UsersChartDrill>(null);
  const [classicDrillDown, setClassicDrillDown] = useState<BandwidthClassicDrillDown>(null);

  const isCountTimeTab = activeCategoryId === 'dashboard';
  const isDatesTab = activeCategoryId === 'dates';
  const isUsersTab = activeCategoryId === 'users';
  const isTasksCustomersTab = activeCategoryId === 'tasks-customers';
  const applyAssigneePageFilters =
    isCountTimeTab || isUsersTab || isTasksCustomersTab || isDatesTab;
  const filterBarMode =
    activeCategoryId === 'dashboard'
      ? 'countTime'
      : activeCategoryId === 'tasks-customers'
        ? 'tasksCustomers'
        : activeCategoryId === 'dates'
          ? 'dates'
          : activeCategoryId === 'users'
            ? 'users'
            : 'tasksCustomers';

  const {
    filterSections,
    filterKey,
    filterValues,
    activeFilterChips,
    customers,
    assignmentGroups,
    setTimeEntryTypes,
    setAvailableActivityTypes,
    handleFilterChange,
    handleRemoveFilterChip: removePageFilterChip,
    handleClearAllFilters: clearAllPageFilters,
  } = useBandwidthFilters(filterBarMode);

  const dashboardQuery = useBandwidthDashboard(
    `${filterKey}__${
      isCountTimeTab
        ? bandwidthCrossFilterQueryKey(crossFilter)
        : isDatesTab
          ? 'dates'
          : isUsersTab
            ? 'users'
            : 'tasks-customers'
    }`,
    filterValues,
    {
      crossFilter: isCountTimeTab ? crossFilter : null,
      applyCountTimePageFilters: isCountTimeTab,
      applyAssignmentGroupFromFilters: isTasksCustomersTab,
      applyAssigneePageFilters,
      applyUsersPageFilters: isUsersTab,
      applyTimeEntryTypeFilter: true,
      datesDrill: isDatesTab ? { month: datesDrillMonth, weekdayMonFirst: datesDrillWeekday } : undefined,
      usersChartDrill: isUsersTab ? usersChartDrill : null,
    }
  );

  const crossFilterChips: FilterChip[] = useMemo(() => {
    if (!isCountTimeTab || !crossFilter) return [];
    const data = dashboardQuery.data;
    if (crossFilter.kind === 'group') {
      const name =
        assignmentGroups.find((g) => g.id === crossFilter.id)?.name ??
        data?.groupUserBreakdown?.find((g) => g.groupId === crossFilter.id)?.groupName ??
        crossFilter.id;
      return [{ id: 'crossFilter', label: 'Teams', value: name, color: 'orange' }];
    }
    if (crossFilter.kind === 'customer') {
      const name =
        customers.find((c) => c.id === crossFilter.id)?.accountName ??
        data?.countsByCustomer?.find((c) => c.customerId === crossFilter.id)?.customer ??
        crossFilter.id;
      return [{ id: 'crossFilter', label: 'Customer', value: name, color: 'teal' }];
    }
    return [];
  }, [isCountTimeTab, crossFilter, dashboardQuery.data, customers, assignmentGroups]);

  const datesDrillChips: FilterChip[] = useMemo(() => {
    if (!isDatesTab) return [];
    const out: FilterChip[] = [];
    if (datesDrillMonth) {
      const d = new Date(`${datesDrillMonth}-01T12:00:00.000Z`);
      const label = d.toLocaleString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
      out.push({ id: 'datesDrillMonth', label: 'Monthwise — Month', value: label, color: 'teal' });
    }
    if (datesDrillWeekday != null && datesDrillWeekday >= 0 && datesDrillWeekday < 7) {
      out.push({
        id: 'datesDrillWeekday',
        label: 'Monthwise — Weekday',
        value: WEEKDAY_LABELS[datesDrillWeekday],
        color: 'blue',
      });
    }
    return out;
  }, [isDatesTab, datesDrillMonth, datesDrillWeekday]);

  const usersDrillChips: FilterChip[] = useMemo(() => {
    if (!isUsersTab || !usersChartDrill) return [];
    if (usersChartDrill.kind === 'week') {
      const label =
        dashboardQuery.data?.activityByWeek?.find((p) => p.weekStart === usersChartDrill.weekStart)?.weekLabel ??
        usersChartDrill.weekStart;
      return [{ id: 'usersChartDrill', label: 'Weekwise — Week', value: label, color: 'purple' }];
    }
    if (usersChartDrill.kind === 'activityType') {
      const types = dashboardQuery.data?.availableActivityTypes ?? [];
      const name =
        types.find((t) => t.value === usersChartDrill.activityType)?.label ?? usersChartDrill.activityType;
      return [{ id: 'usersChartDrill', label: 'Weekwise — Task Types', value: name, color: 'gray' }];
    }
    const custName =
      customers.find((c) => c.id === usersChartDrill.customerId)?.accountName ?? usersChartDrill.customerId;
    return [{ id: 'usersChartDrill', label: 'Weekwise — Customer', value: custName, color: 'teal' }];
  }, [
    isUsersTab,
    usersChartDrill,
    dashboardQuery.data?.activityByWeek,
    dashboardQuery.data?.availableActivityTypes,
    customers,
  ]);

  const classicDrillChips: FilterChip[] = useMemo(() => {
    if (!isTasksCustomersTab || !classicDrillDown) return [];
    if (classicDrillDown.kind === 'heatmap') {
      return [
        { id: 'classicDrillCustomer', label: 'Customer', value: classicDrillDown.colLabel, color: 'teal' as const },
        { id: 'classicDrillType', label: 'Task Types', value: classicDrillDown.rowLabel, color: 'gray' as const },
      ];
    }
    if (classicDrillDown.kind === 'customer') {
      const name =
        customers.find((c) => c.id === classicDrillDown.customerId)?.accountName ?? classicDrillDown.customerId;
      return [{ id: 'classicDrillCustomer', label: 'Customer', value: name, color: 'teal' as const }];
    }
    if (classicDrillDown.kind === 'typeRow') {
      return [
        { id: 'classicDrillType', label: 'Task Types', value: classicDrillDown.rowLabel, color: 'gray' as const },
      ];
    }
    if (classicDrillDown.kind === 'groupHeatmap') {
      return [
        {
          id: 'classicDrillAssignmentGroup',
          label: 'Team',
          value: classicDrillDown.colLabel,
          color: 'orange' as const,
        },
        { id: 'classicDrillType', label: 'Task Types', value: classicDrillDown.rowLabel, color: 'gray' as const },
      ];
    }
    if (classicDrillDown.kind === 'groupBar') {
      return [
        {
          id: 'classicDrillAssignmentGroup',
          label: 'Team',
          value: classicDrillDown.groupLabel,
          color: 'orange' as const,
        },
      ];
    }
    return [];
  }, [isTasksCustomersTab, classicDrillDown, customers]);

  /** Hide page Customer / Type chips when chart drill shows the same dimensions (avoid "All" + drill duplicate). */
  const baseFilterChips = useMemo(() => {
    if (!isTasksCustomersTab || !classicDrillDown) return activeFilterChips;
    return activeFilterChips.filter((c) => {
      if (
        (classicDrillDown.kind === 'heatmap' || classicDrillDown.kind === 'customer') &&
        c.id === 'customer'
      ) {
        return false;
      }
      if (
        (classicDrillDown.kind === 'heatmap' ||
          classicDrillDown.kind === 'groupHeatmap' ||
          classicDrillDown.kind === 'typeRow') &&
        c.id === 'timeEntryType'
      ) {
        return false;
      }
      if (
        (classicDrillDown.kind === 'groupHeatmap' || classicDrillDown.kind === 'groupBar') &&
        c.id === 'assignmentGroup'
      ) {
        return false;
      }
      return true;
    });
  }, [isTasksCustomersTab, activeFilterChips, classicDrillDown]);

  const filterBarChips = useMemo(
    () => [...baseFilterChips, ...crossFilterChips, ...datesDrillChips, ...usersDrillChips, ...classicDrillChips],
    [baseFilterChips, crossFilterChips, datesDrillChips, usersDrillChips, classicDrillChips]
  );

  const handleRemoveFilterChip = useCallback(
    (chipId: string) => {
      if (
        chipId === 'classicDrillCustomer' ||
        chipId === 'classicDrillType' ||
        chipId === 'classicDrillAssignmentGroup'
      ) {
        setClassicDrillDown(null);
        return;
      }
      if (chipId === 'crossFilter') {
        setCrossFilter(null);
        return;
      }
      if (chipId === 'datesDrillMonth') {
        setDatesDrillMonth(null);
        return;
      }
      if (chipId === 'datesDrillWeekday') {
        setDatesDrillWeekday(null);
        return;
      }
      if (chipId === 'usersChartDrill') {
        setUsersChartDrill(null);
        return;
      }
      removePageFilterChip(chipId);
    },
    [removePageFilterChip]
  );

  const handleClearAllFilters = useCallback(() => {
    clearAllPageFilters();
    if (isCountTimeTab) setCrossFilter(null);
    if (isDatesTab) {
      setDatesDrillMonth(null);
      setDatesDrillWeekday(null);
    }
    if (isUsersTab) setUsersChartDrill(null);
    setClassicDrillDown(null);
  }, [clearAllPageFilters, isCountTimeTab, isDatesTab, isUsersTab]);

  useEffect(() => {
    setCrossFilter(null);
    setDatesDrillMonth(null);
    setDatesDrillWeekday(null);
    setUsersChartDrill(null);
    setClassicDrillDown(null);
  }, [filterKey]);

  useEffect(() => {
    if (activeCategoryId !== 'tasks-customers') {
      setClassicDrillDown(null);
    }
  }, [activeCategoryId]);

  useEffect(() => {
    if (!isCountTimeTab) {
      setCrossFilter(null);
    }
  }, [isCountTimeTab]);

  useEffect(() => {
    if (!isUsersTab) {
      setUsersChartDrill(null);
    }
  }, [isUsersTab]);

  useEffect(() => {
    // While the query key changes (e.g. Type filter), TanStack Query can leave `data` undefined
    // until the new request finishes. Treating that as "no types" was clearing the user's selection
    // and dropping timeEntryType from the API params — so the Dates tab type filter looked broken.
    if (!dashboardQuery.data) return;

    const types = dashboardQuery.data.availableActivityTypes ?? [];
    setAvailableActivityTypes(types);
    if (types.length === 0) {
      setTimeEntryTypes((prev) =>
        prev.length === 1 && prev[0] === BW_ALL_TASK_TYPES_FILTER_VALUE
          ? prev
          : [BW_ALL_TASK_TYPES_FILTER_VALUE]
      );
      return;
    }
    const avail = new Set(types.map((a) => a.value));
    setTimeEntryTypes((prev) => {
      let next = prev.filter((t) => t === BW_ALL_TASK_TYPES_FILTER_VALUE || avail.has(t));
      if (next.length === 0) next = [BW_ALL_TASK_TYPES_FILTER_VALUE];
      return next.length === prev.length && next.every((v, i) => v === prev[i]) ? prev : next;
    });
  }, [dashboardQuery.data, setAvailableActivityTypes, setTimeEntryTypes]);

  const activeViewLabel =
    BANDWIDTH_CATEGORIES.find((c) => c.id === activeCategoryId)?.label ?? 'Dashboard';

  const assigneeUserIdsForTables = useMemo(() => {
    if (filterValues.assignedToMe === 'true') return null;
    const ids = normalizeAssignedToUserIds(filterValues.assignedToUserId as string | string[] | undefined);
    return ids.length > 0 ? ids : null;
  }, [filterValues.assignedToMe, filterValues.assignedToUserId]);

  const handleAssigneeUserToggle = useCallback(
    (userId: string) => {
      const me = filterValues.assignedToMe === 'true';
      const current = normalizeAssignedToUserIds(filterValues.assignedToUserId as string | string[] | undefined);
      if (me) {
        handleFilterChange('assignedToMe', '');
        handleFilterChange('assignedToUserId', [userId]);
        setCrossFilter(null);
        return;
      }
      if (current.includes(userId)) {
        handleFilterChange(
          'assignedToUserId',
          current.filter((id) => id !== userId)
        );
      } else {
        handleFilterChange('assignedToUserId', [...current, userId]);
      }
      setCrossFilter(null);
    },
    [filterValues.assignedToMe, filterValues.assignedToUserId, handleFilterChange]
  );

  const pageCustomerId = useMemo(() => {
    const c = String(filterValues.customer ?? 'All');
    return c !== 'All' ? c : null;
  }, [filterValues.customer]);

  const pageSelectedActivityType = useMemo(() => {
    const raw = filterValues.timeEntryType;
    const typesArr = Array.isArray(raw) ? raw : raw ? [String(raw)] : [];
    if (
      typesArr.length === 1 &&
      typesArr[0] !== BW_ALL_TASK_TYPES_FILTER_VALUE
    ) {
      return typesArr[0]!;
    }
    return null;
  }, [filterValues.timeEntryType]);

  const handleTimeEntryTypeToggle = useCallback(
    (activityType: string) => {
      const raw = filterValues.timeEntryType;
      const typesArr = Array.isArray(raw) ? raw : raw ? [String(raw)] : [];
      const isOnlyThis =
        typesArr.length === 1 &&
        typesArr[0] === activityType &&
        typesArr[0] !== BW_ALL_TASK_TYPES_FILTER_VALUE;
      if (isOnlyThis) {
        handleFilterChange('timeEntryType', [BW_ALL_TASK_TYPES_FILTER_VALUE]);
      } else {
        handleFilterChange('timeEntryType', [activityType]);
      }
    },
    [filterValues.timeEntryType, handleFilterChange]
  );

  const exportDateRange = useMemo(
    () =>
      resolveBandwidthEffectiveExportDateRange(
        filterValues as Record<string, string | string[]>,
        dashboardQuery.data
      ),
    [filterValues, dashboardQuery.data]
  );

  const showUpdatingIndicator = useBandwidthUpdatingIndicator(dashboardQuery);

  return (
    <div className="flex h-full min-h-0 flex-col bg-gradient-to-b from-slate-100 via-slate-50/95 to-indigo-50/35">
      {showUpdatingIndicator ? <BandwidthFetchingIndicator /> : null}
      <BandwidthFilterBar
        activeCategoryId={activeCategoryId}
        onCategoryChange={setActiveCategoryId}
        sections={filterSections}
        values={filterValues as Record<string, string | string[]>}
        onFilterChange={handleFilterChange}
        chips={filterBarChips}
        onRemoveChip={handleRemoveFilterChip}
        onClearAllFilters={handleClearAllFilters}
      />

      <div className="relative z-0 min-h-0 flex-1 overflow-auto px-4 pb-8 pt-2 sm:px-5">
        <div className="mx-auto max-w-[1600px]">
          {activeCategoryId === 'dashboard' ? (
            <BandwidthCountTimeGrid
              crossFilter={crossFilter}
              onCrossFilterChange={setCrossFilter}
              assigneeUserIds={assigneeUserIdsForTables}
              onAssigneeUserToggle={handleAssigneeUserToggle}
              pageCustomerId={pageCustomerId}
              selectedActivityType={pageSelectedActivityType}
              onActivityTypeToggle={handleTimeEntryTypeToggle}
              customers={customers}
              dashboardQuery={dashboardQuery}
              exportDateFrom={exportDateRange.dateFrom}
              exportDateTo={exportDateRange.dateTo}
            />
          ) : activeCategoryId === 'tasks-customers' ? (
            <BandwidthClassicDashboardGrid
              filterKey={filterKey}
              filterValues={filterValues}
              customers={customers}
              assignmentGroups={assignmentGroups}
              dashboardQuery={dashboardQuery}
              drillDown={classicDrillDown}
              onDrillDownChange={setClassicDrillDown}
              onAssignmentGroupChange={(groupId) => handleFilterChange('assignmentGroup', groupId)}
              onTimeEntryTypesChange={(types) => handleFilterChange('timeEntryType', types)}
            />
          ) : activeCategoryId === 'dates' ? (
            <BandwidthDatesGrid
              dashboardQuery={dashboardQuery}
              datesDrillMonth={datesDrillMonth}
              datesDrillWeekday={datesDrillWeekday}
              onToggleDrillMonth={(ym) => setDatesDrillMonth((m) => (m === ym ? null : ym))}
              onToggleDrillWeekday={(i) => setDatesDrillWeekday((w) => (w === i ? null : i))}
              exportDateFrom={exportDateRange.dateFrom}
              exportDateTo={exportDateRange.dateTo}
            />
          ) : activeCategoryId === 'users' ? (
            <BandwidthUsersGrid
              dashboardQuery={dashboardQuery}
              usersChartDrill={usersChartDrill}
              onUsersChartDrillChange={setUsersChartDrill}
              exportDateFrom={exportDateRange.dateFrom}
              exportDateTo={exportDateRange.dateTo}
            />
          ) : (
            <div className="flex min-h-[420px] flex-col items-center justify-center rounded-2xl border border-slate-200/90 bg-gradient-to-br from-white via-slate-50/50 to-indigo-50/20 p-12 text-center shadow-lg shadow-slate-900/5 ring-1 ring-slate-950/[0.04]">
              <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-100 to-violet-100 shadow-inner">
                <ChartBarIcon className="h-10 w-10 text-indigo-500" aria-hidden />
              </div>
              <h2 className="mt-6 text-lg font-semibold tracking-tight text-slate-800">{activeViewLabel}</h2>
              <p className="mt-2 max-w-sm text-sm leading-relaxed text-slate-500">
                This view will show Bandwidth analytics for this category when data is connected.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Bandwidth;
